// frontend/src/pages/QuickTrade.jsx

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axiosAuth from "../axiosConfig";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const QuickTrade = () => {
  const navigate = useNavigate();

  const [user, setUser]                 = useState(null);
  const [balances, setBalances]         = useState({});
  const [price, setPrice]               = useState(0);
  const [selectedCoin, setSelectedCoin] = useState("USDT");
  const [tradeType, setTradeType]       = useState("BUY");

  const [inrBuy, setInrBuy]             = useState("");
  const [coinBuy, setCoinBuy]           = useState("");
  const [inrSellGross, setInrSellGross] = useState("");
  const [coinSell, setCoinSell]         = useState("");

  const [loadingPrice, setLoadingPrice] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [previewInfo, setPreviewInfo]   = useState(null);

  const apiRoot = (process.env.REACT_APP_API_URL || "").replace(/\/api\/auth\/?$/, "");
  const tradesAPI = axios.create({
    baseURL: apiRoot ? `${apiRoot}/api/trades` : "/api/trades",
    withCredentials: true,
  });

  useEffect(() => {
    (async () => {
      try {
        const who = await axiosAuth.get("/whoami");
        const usr = who.data.user || who.data;
        setUser(usr);

        const bal = await axiosAuth.get(`/balance/${encodeURIComponent(usr.email)}`);
        setBalances(bal.data.balances ?? bal.data ?? {});
      } catch (err) {
        console.error("QuickTrade fetch error:", err);
        navigate("/login");
      }
    })();
  }, [navigate]);

  useEffect(() => {
    const fetchPrice = async () => {
      setLoadingPrice(true);
      try {
        let cgId = "";
        if (selectedCoin === "USDT") cgId = "tether";
        if (selectedCoin === "BTC")  cgId = "bitcoin";
        if (selectedCoin === "ETH")  cgId = "ethereum";

        if (!cgId) {
          setPrice(0);
        } else {
          const resp = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${cgId}&vs_currencies=inr`);
          const json = await resp.json();
          setPrice(json[cgId]?.inr ?? 0);
        }
      } catch (err) {
        console.error("QuickTrade price fetch error:", err);
        setPrice(0);
      } finally {
        setLoadingPrice(false);
      }
    };

    setInrBuy(""); setCoinBuy("");
    setInrSellGross(""); setCoinSell("");
    setPreviewInfo(null);

    fetchPrice();
  }, [selectedCoin]);

  const syncBuy = useRef(false);
  useEffect(() => {
    if (tradeType !== "BUY") return;
    if (syncBuy.current) { syncBuy.current = false; return; }
    const v = parseFloat(inrBuy);
    if (!isNaN(v) && price > 0) {
      syncBuy.current = true;
      setCoinBuy((v / price).toFixed(6));
    } else {
      syncBuy.current = true;
      setCoinBuy("");
    }
  }, [inrBuy, price, tradeType]);
  useEffect(() => {
    if (tradeType !== "BUY") return;
    if (syncBuy.current) { syncBuy.current = false; return; }
    const v = parseFloat(coinBuy);
    if (!isNaN(v) && price > 0) {
      syncBuy.current = true;
      setInrBuy((v * price).toFixed(2));
    } else {
      syncBuy.current = true;
      setInrBuy("");
    }
  }, [coinBuy, price, tradeType]);

  const syncSell = useRef(false);
  useEffect(() => {
    if (tradeType !== "SELL") return;
    if (syncSell.current) { syncSell.current = false; return; }
    const v = parseFloat(inrSellGross);
    if (!isNaN(v) && price > 0) {
      syncSell.current = true;
      setCoinSell((v / price).toFixed(6));
    } else {
      syncSell.current = true;
      setCoinSell("");
    }
  }, [inrSellGross, price, tradeType]);
  useEffect(() => {
    if (tradeType !== "SELL") return;
    if (syncSell.current) { syncSell.current = false; return; }
    const v = parseFloat(coinSell);
    if (!isNaN(v) && price > 0) {
      syncSell.current = true;
      setInrSellGross((v * price).toFixed(2));
    } else {
      syncSell.current = true;
      setInrSellGross("");
    }
  }, [coinSell, price, tradeType]);

  const handleBuyMax = () => {
    const B = Number(balances.INR ?? 0);
    const preFee = B / (1 + 0.005 + 0.005 * 0.18);
    setInrBuy(preFee.toFixed(2));
    setPreviewInfo(null);
  };
  const handleSellMax = () => {
    const avail = Number(balances[selectedCoin] ?? 0);
    setCoinSell(avail.toFixed(6));
    setPreviewInfo(null);
  };

  const computePreview = () => {
    setPreviewInfo(null);

    if (tradeType === "BUY") {
      const inrVal = parseFloat(inrBuy);
      if (isNaN(inrVal) || inrVal <= 0 || price <= 0) {
        return toast.error("Enter a valid INR amount or coin quantity");
      }
      const coinQty   = parseFloat(coinBuy);
      const fee       = 0.005 * inrVal;
      const gst       = 0.18 * fee;
      const totalCost = inrVal + fee + gst;

      setPreviewInfo({ type: "BUY", coinQty: Number(coinQty), fee, gst, totalCost });
    } else {
      const coinVal = parseFloat(coinSell);
      if (isNaN(coinVal) || coinVal <= 0 || price <= 0) {
        return toast.error("Enter a valid coin quantity or gross INR");
      }
      const grossINR    = coinVal * price;
      const platformFee = 0.005 * grossINR;
      const gst         = 0.18 * platformFee;
      const tds         = 0.01 * grossINR;
      const netINR      = grossINR - (platformFee + gst + tds);

      setPreviewInfo({ type: "SELL", coinQty: Number(coinVal), grossINR, platformFee, gst, tds, netINR });
    }
  };
  const clearPreview = () => setPreviewInfo(null);

  const handleConfirm = async () => {
    if (!previewInfo || price <= 0) return;
    setPlacingOrder(true);
    try {
      const { type, coinQty } = previewInfo;
      const res = await tradesAPI.post("/new-order", {
        type,
        pair: `${selectedCoin}-INR`,
        amount: coinQty,
        price,
      });
      if (res.data.success) {
        toast.success(`Order placed: ${res.data.filled ? "Filled" : "Pending"}`);
        const bal = await axiosAuth.get(`/balance/${encodeURIComponent(user.email)}`);
        setBalances(bal.data.balances ?? bal.data ?? {});
        setInrBuy(""); setCoinBuy("");
        setInrSellGross(""); setCoinSell("");
        clearPreview();
      } else {
        toast.error(res.data.message || "Order failed");
      }
    } catch (err) {
      console.error("QuickTrade confirm error:", err);
      toast.error("Failed to place order");
    } finally {
      setPlacingOrder(false);
    }
  };

  // Ensure all five entries exist and in fixed order (dashboard-style)
  const balanceOrder = ["INR", "USDT", "BTC", "ETH", "USDC"];
  const getAmt = (k) => Number(balances?.[k] ?? 0);

  return (
    <motion.div
      className="min-h-screen bg-blue-50 dark:bg-black pb-8 md:pb-10 text-gray-900 dark:text-white"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 shadow-lg border-b-4 border-blue-600">
        <div className="max-w-7xl mx-auto w-full px-4 md:px-6 py-4 md:py-6 flex flex-col sm:flex-row justify-between items-start sm:items-center">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-blue-800 dark:text-blue-400 mb-0.5 md:mb-1">Quick Trade</h1>
            <p className="text-blue-600 dark:text-blue-300 text-sm md:text-base">Enter INR or coin quantity below</p>
          </div>

        </div>
      </header>

      <main className="max-w-7xl mx-auto w-full px-3 sm:px-6 mt-3 sm:mt-6 space-y-3 sm:space-y-8">
        {/* Quick Actions (compact on phone, consistent sizing) */}
        <motion.div
          className="bg-white dark:bg-gray-900 rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg p-3 sm:p-6 flex flex-col sm:flex-row items-center sm:justify-between gap-3 sm:gap-4 border-l-4 border-blue-500"
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-blue-700 dark:text-blue-300 font-medium text-sm sm:text-base">Quick Actions</p>
          <div className="flex flex-nowrap gap-2 sm:gap-3 overflow-x-auto scrollbar-hide md:ml-auto">
            <button
              onClick={() => navigate("/deposit")}
              className="bg-blue-600 text-white px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-base rounded-lg sm:rounded-xl hover:bg-blue-700 transition shadow-sm whitespace-nowrap"
            >
              Deposit
            </button>
            <button
              onClick={() => navigate("/withdraw")}
              className="bg-blue-600 text-white px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-base rounded-lg sm:rounded-xl hover:bg-blue-700 transition shadow-sm whitespace-nowrap"
            >
              Withdraw
            </button>
            <button
              onClick={() => navigate("/exchange")}
              className="bg-blue-600 text-white px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-base rounded-lg sm:rounded-xl hover:bg-blue-700 transition shadow-sm whitespace-nowrap"
            >
              Exchange
            </button>
            <button
              onClick={() => navigate("/p2p")}
              className="bg-blue-600 text-white px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-base rounded-lg sm:rounded-xl hover:bg-blue-700 transition shadow-sm whitespace-nowrap"
            >
              P2P Market
            </button>
          </div>
        </motion.div>

        {/* Balances
            - Phone: 3 columns → exactly 2 rows (5 cards → 3+2), square cards
            - Desktop: unchanged (2 / 4 columns, no forced squares)
        */}
        <motion.div
          className="bg-white dark:bg-gray-900 md:rounded-2xl rounded-xl md:shadow-lg shadow md:p-6 p-4 border-t-4 border-blue-500"
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.5 }}
        >
          <h3 className="text-lg md:text-xl font-semibold text-blue-800 dark:text-blue-400 mb-3 md:mb-4">Your Balances</h3>

          {/* Phone-only squares in two rows */}
          <div className="grid grid-cols-3 gap-2 sm:hidden">
            {balanceOrder.map((curr) => (
              <div
                key={`m-${curr}`}
                className="bg-blue-50 dark:bg-gray-800 rounded-lg border-t-4 border-blue-600 shadow-sm aspect-square flex items-center justify-center"
              >
                <div className="text-center px-1">
                  <div className="text-sm font-bold text-blue-700 dark:text-blue-300">{curr}</div>
                  <div className="text-[12px] font-semibold text-blue-800 dark:text-white mt-0.5">
                    {curr === "INR"
                      ? `₹ ${getAmt(curr).toFixed(2)}`
                      : `${getAmt(curr).toFixed(6)} ${curr}`}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop / tablet unchanged layout */}
          <div className="hidden sm:grid grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries({
              INR: getAmt("INR"),
              USDT: getAmt("USDT"),
              BTC: getAmt("BTC"),
              ETH: getAmt("ETH"),
              USDC: getAmt("USDC"),
            }).map(([curr, amt]) => (
              <div
                key={`d-${curr}`}
                className="bg-blue-50 dark:bg-gray-800 rounded-lg p-4 flex flex-col items-center justify-between border-t-4 border-blue-600 shadow-sm"
              >
                <span className="text-xl font-bold text-blue-700 dark:text-blue-300">{curr}</span>
                <span className="text-2xl font-bold text-blue-800 dark:text-white">
                  {curr === "INR" ? `₹ ${Number(amt).toFixed(2)}` : `${Number(amt).toFixed(6)} ${curr}`}
                </span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Trade Form (unchanged desktop, compact phone) */}
        <motion.div
          className="bg-white dark:bg-gray-900 md:rounded-2xl rounded-xl md:shadow-lg shadow md:p-6 p-4 border-t-4 border-blue-500"
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <div className="mb-4 md:mb-6">
            <div className="w-full sm:w-1/2">
              <label className="block text-sm md:text-lg font-medium text-blue-700 dark:text-blue-300 mb-1.5 md:mb-2">Select Coin</label>
              <select
                value={selectedCoin}
                onChange={(e) => { setSelectedCoin(e.target.value); clearPreview(); }}
                className="w-full border border-gray-300 dark:border-gray-700 dark:bg-gray-800 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm md:text-base text-gray-900 dark:text-white"
              >
                <option value="USDT">USDT</option>
                <option value="BTC">BTC</option>
                <option value="ETH">ETH</option>
              </select>
            </div>
          </div>

          <div className="mb-4 md:mb-6">
            <span className="text-sm md:text-lg text-blue-800 dark:text-blue-400">
              {selectedCoin} Price:{" "}
              {loadingPrice ? (
                <span className="italic text-gray-500 dark:text-gray-400">Loading...</span>
              ) : (
                <span className="font-semibold">
                  ₹ {Number(price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              )}
            </span>
          </div>

          {tradeType === "BUY" ? (
            <>
              <div className="mb-3 md:mb-4 max-w-md relative">
                <label className="block text-sm md:text-lg font-medium text-blue-700 dark:text-blue-300 mb-1.5 md:mb-2">Amount in INR (pre-fee)</label>
                <input
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={inrBuy}
                  onChange={(e) => { setInrBuy(e.target.value); clearPreview(); }}
                  placeholder="₹ 0.00"
                  className="w-full pr-16 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm md:text-base bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
                <button
                  type="button"
                  onClick={handleBuyMax}
                  className="absolute top-1/2 right-2 -translate-y-1/2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 text-xs md:text-sm font-semibold px-2.5 md:px-3 py-1 rounded"
                >
                  Max
                </button>
                <p className="text-[12px] md:text-sm text-gray-500 dark:text-gray-400 mt-1">Available: ₹ {Number(balances.INR ?? 0).toFixed(2)}</p>
              </div>

              <div className="mb-4 md:mb-6 max-w-md">
                <label className="block text-sm md:text-lg font-medium text-blue-700 dark:text-blue-300 mb-1.5 md:mb-2">Coin Quantity</label>
                <input
                  type="number"
                  step="0.000001"
                  inputMode="decimal"
                  value={coinBuy}
                  onChange={(e) => { setCoinBuy(e.target.value); clearPreview(); }}
                  placeholder={`0.000000 ${selectedCoin}`}
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm md:text-base bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
            </>
          ) : (
            <>
              <div className="mb-3 md:mb-4 max-w-md relative">
                <label className="block text-sm md:text-lg font-medium text-blue-700 dark:text-blue-300 mb-1.5 md:mb-2">Coin Quantity</label>
                <input
                  type="number"
                  step="0.000001"
                  inputMode="decimal"
                  value={coinSell}
                  onChange={(e) => { setCoinSell(e.target.value); clearPreview(); }}
                  placeholder={`0.000000 ${selectedCoin}`}
                  className="w-full pr-16 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm md:text-base bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
                <button
                  type="button"
                  onClick={handleSellMax}
                  className="absolute top-1/2 right-2 -translate-y-1/2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 text-xs md:text-sm font-semibold px-2.5 md:px-3 py-1 rounded"
                >
                  Max
                </button>
                <p className="text-[12px] md:text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Available: {Number(balances[selectedCoin] ?? 0).toFixed(6)} {selectedCoin}
                </p>
              </div>

              <div className="mb-4 md:mb-6 max-w-md">
                <label className="block text-sm md:text-lg font-medium text-blue-700 dark:text-blue-300 mb-1.5 md:mb-2">Gross INR (before fees)</label>
                <input
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={inrSellGross}
                  onChange={(e) => { setInrSellGross(e.target.value); clearPreview(); }}
                  placeholder="₹ 0.00"
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm md:text-base bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
            </>
          )}

          <div className="flex gap-2 md:gap-4 mb-3 md:mb-4">
            <button
              onClick={() => { setTradeType("BUY"); clearPreview(); }}
              className={`flex-1 py-2 rounded-lg font-medium text-sm md:text-base ${
                tradeType === "BUY" ? "text-white bg-green-600 hover:bg-green-700" : "bg-white dark:bg-gray-800 text-gray-700 dark:text-white border border-gray-300 dark:border-gray-700"
              } transition`}
            >
              Buy
            </button>
            <button
              onClick={() => { setTradeType("SELL"); clearPreview(); }}
              className={`flex-1 py-2 rounded-lg font-medium text-sm md:text-base ${
                tradeType === "SELL" ? "text-white bg-red-600 hover:bg-red-700" : "bg-white dark:bg-gray-800 text-gray-700 dark:text-white border border-gray-300 dark:border-gray-700"
              } transition`}
            >
              Sell
            </button>
          </div>

          <div className="flex gap-2 md:gap-4 mb-4 md:mb-6">
            <button
              onClick={computePreview}
              disabled={
                loadingPrice ||
                (tradeType === "BUY" ? !inrBuy || isNaN(Number(inrBuy)) : !coinSell || isNaN(Number(coinSell)))
              }
              className={`flex-1 py-2 rounded-lg text-white text-sm md:text-base ${
                loadingPrice ||
                (tradeType === "BUY" ? !inrBuy || isNaN(Number(inrBuy)) : !coinSell || isNaN(Number(coinSell)))
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              } transition`}
            >
              {tradeType === "BUY" ? "Preview Buy" : "Preview Sell"}
            </button>
            {previewInfo && (
              <button
                onClick={handleConfirm}
                disabled={placingOrder}
                className={`flex-1 py-2 rounded-lg text-white text-sm md:text-base ${
                  placingOrder
                    ? "bg-gray-400 cursor-not-allowed"
                    : tradeType === "BUY"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-red-600 hover:bg-red-700"
                } transition`}
              >
                {placingOrder ? "Placing…" : tradeType === "BUY" ? "Confirm Buy" : "Confirm Sell"}
              </button>
            )}
          </div>

          {previewInfo?.type === "BUY" && (
            <div className="max-w-md bg-blue-50 dark:bg-gray-800 md:p-4 p-3 rounded-lg border border-blue-200 dark:border-gray-700 mb-1.5 md:mb-2 text-sm md:text-base">
              <p className="text-blue-800 dark:text-blue-300">Coin Qty: <span className="font-semibold">{previewInfo.coinQty.toFixed(6)} {selectedCoin}</span></p>
              <p className="text-blue-800 dark:text-blue-300">Platform Fee (0.5%): <span className="font-semibold">₹ {previewInfo.fee.toFixed(2)}</span></p>
              <p className="text-blue-800 dark:text-blue-300">GST (18% on fee): <span className="font-semibold">₹ {previewInfo.gst.toFixed(2)}</span></p>
              <p className="text-blue-800 dark:text-blue-300"><strong>Total Cost:</strong> <span className="font-semibold">₹ {previewInfo.totalCost.toFixed(2)}</span></p>
            </div>
          )}

          {previewInfo?.type === "SELL" && (
            <div className="max-w-md bg-blue-50 dark:bg-gray-800 md:p-4 p-3 rounded-lg border border-blue-200 dark:border-gray-700 mb-1.5 md:mb-2 text-sm md:text-base">
              <p className="text-blue-800 dark:text-blue-300">Gross INR: <span className="font-semibold">₹ {previewInfo.grossINR.toFixed(2)}</span></p>
              <p className="text-blue-800 dark:text-blue-300">Platform Fee (0.5%): <span className="font-semibold">₹ {previewInfo.platformFee.toFixed(2)}</span></p>
              <p className="text-blue-800 dark:text-blue-300">GST (18% on fee): <span className="font-semibold">₹ {previewInfo.gst.toFixed(2)}</span></p>
              <p className="text-blue-800 dark:text-blue-300">TDS (1%): <span className="font-semibold">₹ {previewInfo.tds.toFixed(2)}</span></p>
              <p className="text-blue-800 dark:text-blue-300"><strong>Net INR to Wallet:</strong> <span className="font-semibold">₹ {previewInfo.netINR.toFixed(2)}</span></p>
            </div>
          )}
        </motion.div>
      </main>
    </motion.div>
  );
};

export default QuickTrade;
